import React from 'react';
import { Calendar, Clock, Users, Trophy, CloudRain, Star, Flag, FileText } from 'lucide-react';
import dayjs from 'dayjs';
import { Card } from './ui';
import { formatTime } from '../utils/formatTime';
import recapsData from '../utils/recapsData';

/**
 * EventCard - Card component for displaying golf events
 *
 * Displays event information in a compact card format:
 * - Course name (prominent)
 * - Date with day of week
 * - Tee time
 * - Signup count
 * - Status badges (Major, Non-FedUp, Rainout)
 * - Winner for past events
 */
const EventCard = ({
  event,
  signupCount = 0,
  isUpcoming = false,
  onClick,
  isSelected = false,
  showImage = true,
}) => {
  const eventDate = dayjs(event.date);
  const isToday = eventDate.isSame(dayjs(), 'day');
  const isTomorrow = eventDate.isSame(dayjs().add(1, 'day'), 'day');
  const isThisWeek = eventDate.isAfter(dayjs()) && eventDate.isBefore(dayjs().add(7, 'day'));

  // Determine card accent based on event status
  const getLeftBorder = () => {
    if (event.rained_out) return 'sunset';
    if (isToday) return 'forest';
    if (event.is_major) return 'gold';
    if (isUpcoming) return null;
    return null;
  };

  // Format date display
  const formatDateDisplay = () => {
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    return eventDate.format('ddd, MMM D');
  };

  // Get relative date badge
  const getDateBadge = () => {
    if (isToday) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-forest-500 text-white animate-pulse">
          TODAY
        </span>
      );
    }
    if (isTomorrow) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500 text-white">
          TOMORROW
        </span>
      );
    }
    if (isThisWeek && isUpcoming) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
          This Week
        </span>
      );
    }
    return null;
  };

  // Check if we have a course image
  const courseImage = event.course_image;
  const hasImage = showImage && courseImage;

  // Check if this event has a recap
  const recapId = eventDate.format('YYYY-MM-DD');
  const eventRecap = recapsData.find(r => r.id === recapId);

  return (
    <Card
      interactive
      leftBorder={getLeftBorder()}
      onClick={onClick}
      className={`
        group relative overflow-hidden
        ${isSelected ? 'ring-2 ring-forest-500 dark:ring-forest-400' : ''}
        ${isToday && !hasImage ? 'bg-gradient-to-r from-forest-50 to-white dark:from-forest-900/20 dark:to-navy-900' : ''}
      `}
    >
      {/* Background Image with Overlay */}
      {hasImage && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-105"
            style={{ backgroundImage: `url(${courseImage})` }}
          />
          <div className={`
            absolute inset-0
            ${isToday
              ? 'bg-gradient-to-r from-forest-900/85 to-forest-800/80'
              : 'bg-gradient-to-br from-white/90 via-white/85 to-white/80 dark:from-navy-900/90 dark:via-navy-900/85 dark:to-navy-800/80'
            }
          `} />
        </>
      )}

      {/* Content - needs relative positioning to appear above background */}
      <div className="relative">
      {/* Top Row - Date Badge & Status Badges */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          {getDateBadge()}
          {event.is_major && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gold-400/20 text-gold-700 dark:text-gold-400 border border-gold-400/30">
              <Star className="w-3 h-3" />
              Major
            </span>
          )}
          {!event.fedup_eligible && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              Non-FedUp
            </span>
          )}
          {event.rained_out && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400">
              <CloudRain className="w-3 h-3" />
              Rainout
            </span>
          )}
        </div>

        {/* Signup Count Badge - Only for upcoming */}
        {isUpcoming && (
          <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span className="font-semibold tabular-nums">{signupCount}</span>
          </div>
        )}
      </div>

      {/* Course Name - Main Focus */}
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-forest-700 dark:group-hover:text-forest-400 transition-colors">
        {event.course_name}
      </h3>

      {/* Event Details Row */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
        {/* Date */}
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <span className="font-medium">
            {formatDateDisplay()}
            {!isToday && !isTomorrow && (
              <span className="text-gray-400 dark:text-gray-500 ml-1">
                {eventDate.format('YYYY')}
              </span>
            )}
          </span>
        </div>

        {/* Tee Time */}
        {event.tee_time && (
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <span className="font-semibold text-forest-600 dark:text-forest-400">
              {formatTime(event.tee_time)}
            </span>
          </div>
        )}

        {/* Cost - Only for upcoming */}
        {isUpcoming && event.cost != null && (
          <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <span className="font-semibold">${Number(event.cost || 0).toFixed(0)}</span>
          </div>
        )}
      </div>

      {/* Winner - Only for past events */}
      {!isUpcoming && event.winner_name && !event.rained_out && (
        <div className={`mt-3 pt-3 border-t ${hasImage ? 'border-gray-300/30 dark:border-white/10' : 'border-gray-100 dark:border-navy-800'}`}>
          <div className="flex items-center gap-2 text-sm">
            <Trophy className="w-4 h-4 text-gold-500" />
            <span className={hasImage ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'}>Winner:</span>
            <span className="font-semibold text-gray-900 dark:text-white">
              {event.winner_name}
            </span>
          </div>
        </div>
      )}

      {/* Recap Link - Only for past events with recaps */}
      {!isUpcoming && eventRecap && (
        <div className={`mt-3 pt-3 border-t ${hasImage ? 'border-gray-300/30 dark:border-white/10' : 'border-gray-100 dark:border-navy-800'}`}>
          <a
            href={`/recaps/${eventRecap.id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 text-sm font-medium text-forest-600 dark:text-forest-400 hover:text-forest-700 dark:hover:text-forest-300 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Read Event Recap
          </a>
        </div>
      )}
      </div>

      {/* Hover Indicator */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-8 h-8 rounded-full bg-forest-100 dark:bg-forest-900/40 flex items-center justify-center">
          <Flag className="w-4 h-4 text-forest-600 dark:text-forest-400" />
        </div>
      </div>
    </Card>
  );
};

export default EventCard;
